import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { FileDownloadOutlined as ExportIcon } from "@mui/icons-material";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import { toast } from "react-hot-toast";
import { PaginatedTable } from "../../../components";
import { baseAxios } from "../../../utils/config";

function compareRows(a, b, field) {
  const va = a[field];
  const vb = b[field];
  if (typeof va === "number" || typeof vb === "number") {
    return (Number(va) || 0) - (Number(vb) || 0);
  }
  return String(va ?? "").localeCompare(String(vb ?? ""), undefined, {
    sensitivity: "base",
  });
}

/**
 * Product Usage analytics table
 */
export function ProductUsageTab({
  salon,
  startDate,
  endDate,
  formatDateToISO,
  user,
  needsLocation,
  refreshKey,
  hasRun,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState("times_used");
  const [sortOrder, setSortOrder] = useState("desc");

  const loadData = useCallback(async () => {
    if (needsLocation || !salon) {
      setRows([]);
      return;
    }

    setLoading(true);
    try {
      const params = {
        locationId: salon?.location_id || user.location_id,
      };
      if (startDate) params.startDate = formatDateToISO(startDate);
      if (endDate) params.endDate = formatDateToISO(endDate);

      const response = await baseAxios.get(
        "/formula-tracker/analytics/products",
        { params }
      );
      setRows(Array.isArray(response.data) ? response.data : []);
    } catch {
      setRows([]);
      toast.error("Failed to load product usage data", { duration: 3000 });
    } finally {
      setLoading(false);
    }
  }, [
    endDate,
    formatDateToISO,
    needsLocation,
    salon,
    startDate,
    user.location_id,
  ]);

  useEffect(() => {
    loadData();
  }, [loadData, refreshKey]);

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const c = compareRows(a, b, sortBy);
      return sortOrder === "asc" ? c : -c;
    });
    return copy;
  }, [rows, sortBy, sortOrder]);

  const handleSortChange = useCallback((field, order) => {
    setSortBy(field);
    setSortOrder(order);
  }, []);

  const noopPagination = useCallback(() => {}, []);

  const handleExport = useCallback(async () => {
    if (!sortedRows.length) {
      toast.error("No data to export", { duration: 2500 });
      return;
    }
    try {
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet("Product Usage");
      worksheet.columns = [
        { header: "Product", key: "product_name", width: 24 },
        { header: "Brand", key: "brand", width: 16 },
        { header: "Times Used", key: "times_used", width: 12 },
        { header: "Total Amount", key: "total_amount", width: 14 },
        { header: "Avg per Use", key: "avg_amount_per_use", width: 14 },
        { header: "Commonly Paired With", key: "paired_with", width: 28 },
      ];
      worksheet.getRow(1).font = { bold: true };
      sortedRows.forEach((row) => {
        const unit = row.unit || "";
        worksheet.addRow({
          ...row,
          total_amount:
            row.total_amount == null
              ? "0"
              : `${Number(row.total_amount).toFixed(2)} ${unit}`.trim(),
          avg_amount_per_use:
            row.avg_amount_per_use == null
              ? "0"
              : `${Number(row.avg_amount_per_use).toFixed(2)} ${unit}`.trim(),
          paired_with: Array.isArray(row.paired_with)
            ? row.paired_with.slice(0, 3).join(", ")
            : "",
        });
      });
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `ProductUsage_${dayjs().format("YYYY-MM-DD")}.xlsx`
      );
    } catch {
      toast.error("Export failed", { duration: 3000 });
    }
  }, [sortedRows]);

  const columns = useMemo(
    () => [
      {
        id: "product_name",
        label: "Product",
        field: "product_name",
        minWidth: 180,
        render: (v) => v || "—",
      },
      {
        id: "brand",
        label: "Brand",
        field: "brand",
        minWidth: 130,
        render: (v) => v || "—",
      },
      {
        id: "times_used",
        label: "Times Used",
        field: "times_used",
        minWidth: 110,
        align: "center",
        render: (v) => Number(v) || 0,
      },
      {
        id: "total_amount",
        label: "Total Amount",
        field: "total_amount",
        minWidth: 130,
        align: "right",
        render: (v, row) => {
          if (v == null || Number.isNaN(Number(v))) return "0";
          return `${Number(v).toFixed(2)} ${row?.unit || ""}`.trim();
        },
      },
      {
        id: "avg_amount_per_use",
        label: "Avg per Use",
        field: "avg_amount_per_use",
        minWidth: 120,
        align: "right",
        render: (v, row) => {
          if (v == null || Number.isNaN(Number(v))) return "0";
          return `${Number(v).toFixed(2)} ${row?.unit || ""}`.trim();
        },
      },
      {
        id: "paired_with",
        label: "Commonly Paired With",
        field: "paired_with",
        minWidth: 200,
        render: (v) =>
          Array.isArray(v) && v.length ? v.slice(0, 3).join(", ") : "—",
      },
    ],
    []
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          mb: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Typography fontWeight={600}>Product Usage Analytics</Typography>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<ExportIcon />}
          onClick={handleExport}
          disabled={loading || !sortedRows.length}
          sx={{ textTransform: "none", fontWeight: 600, minHeight: 36 }}
        >
          Export
        </Button>
      </Box>
      <PaginatedTable
        columns={columns}
        rows={sortedRows}
        totalRowCount={sortedRows.length}
        loading={loading}
        onPaginationChange={noopPagination}
        onSortChange={handleSortChange}
        initialOrderBy="times_used"
        initialOrder="desc"
        searchQuery={`prod|${refreshKey}|${salon?.location_id}|${formatDateToISO(startDate)}|${formatDateToISO(endDate)}`}
        clientSidePagination
        skeletonHeight="40px"
        getRowKey={(row, i) => row.product_id ?? `product-${i}`}
        emptyMessage={
          hasRun
            ? "No product usage data for this range"
            : "Click Load Analytics to view data"
        }
      />
    </Box>
  );
}
